import { useEffect } from 'react';
import { useDispatch, useSelector } from 'store/hooks';
import { getCommunityCredits } from 'store/community/actionCreators';
import { RootState } from '../store';

type PlatformPrefix = 'DIS' | 'TEL';

// In-flight requests de-duplication across all hook instances
const inFlightCommunityCredits = new Set<string>();

export default function useCommunityCredits(
  platformPrefix: PlatformPrefix,
  communityId?: string,
) {
  const dispatch = useDispatch();

  const encodedCommunityId = communityId
    ? encodeURIComponent(`${platformPrefix}#COMM#${communityId}`)
    : undefined;

  const credits = useSelector((state: RootState) =>
    encodedCommunityId
      ? state.community.communityCredits[encodedCommunityId]
      : undefined,
  );

  useEffect(() => {
    if (!encodedCommunityId) return;
    if (credits) return; // Already cached
    if (inFlightCommunityCredits.has(encodedCommunityId)) return; // Request already in progress

    inFlightCommunityCredits.add(encodedCommunityId);
    Promise.resolve(dispatch(getCommunityCredits(encodedCommunityId))).finally(
      () => {
        inFlightCommunityCredits.delete(encodedCommunityId);
      },
    );
  }, [dispatch, encodedCommunityId, credits]);

  return { credits };
}
